<script setup lang="ts">
import type { HomeType } from '~/composables/useHomeType'
import { DIRECT_HUB_PATH } from '~~/shared/establishment-filters'

/**
 * BookDirectStepsV3 — « Réservez en direct, sans commission » en 3 étapes.
 *
 * Formule l'argument « sans commission » côté VOYAGEUR (le pitch propriétaire
 * vit plus bas sur la home). Rendu 100 % SSR, texte statique i18n : un H2 et
 * trois H3 ordonnés. Le texte de la 1re étape suit le type actif de la home.
 */
const props = withDefaults(defineProps<{
  type?: HomeType
}>(), {
  type: 'all',
})

const STEP_KEYS = ['find', 'contact', 'pay'] as const

const { t, locale } = useI18n()
const localePath = useLocalePath()

// Liens de la 1re étape : points d'entrée du catalogue (maillage SSR).
const findLinks = computed(() => [
  { to: localePath('/regions'), label: t('pages.home.bookDirect.steps.find.linkRegions') },
  { to: localePath('/departments'), label: t('pages.home.bookDirect.steps.find.linkDepartments') },
])

// Lien de l'étape « contact » vers le hub « en direct » (4.146.0). La page est
// FR uniquement : en EN, `/en/chambres-hotes/en-direct` serait une 404, donc
// aucun lien n'est rendu.
const contactLinks = computed(() => locale.value === 'fr'
  ? [{ to: localePath(DIRECT_HUB_PATH), label: t('pages.home.bookDirect.steps.contact.linkDirect') }]
  : [])

const stepLinks = computed<Record<(typeof STEP_KEYS)[number], Array<{ to: string, label: string }>>>(() => ({
  find: findLinks.value,
  contact: contactLinks.value,
  pay: [],
}))

const steps = computed(() => STEP_KEYS.map(key => ({
  key,
  title: t(`pages.home.bookDirect.steps.${key}.title`),
  text: key === 'find'
    ? t(`pages.home.bookDirect.steps.find.text.${props.type}`)
    : t(`pages.home.bookDirect.steps.${key}.text`),
})))
</script>

<template>
  <section
    id="book-direct"
    class="py-20 md:py-28 bg-paper-100 text-paper-900 border-y border-paper-300/60"
    aria-labelledby="book-direct-title"
  >
    <div class="container mx-auto px-4 lg:px-8">
      <header class="max-w-2xl mb-10 md:mb-14">
        <p class="tag text-sage-700 mb-4">
          {{ $t('pages.home.bookDirect.eyebrow') }}
        </p>
        <h2
          id="book-direct-title"
          class="font-display font-light tracking-[-0.02em] leading-tight text-3xl md:text-5xl text-paper-900 mb-4"
        >
          {{ $t('pages.home.bookDirect.title') }}
        </h2>
        <p class="font-sans text-base md:text-lg text-paper-900/70 leading-relaxed">
          {{ $t('pages.home.bookDirect.subtitle') }}
        </p>
      </header>

      <ol class="max-w-[1120px] grid grid-cols-1 md:grid-cols-3 gap-6">
        <li
          v-for="(step, i) in steps"
          :key="step.key"
          class="rounded-2xl border hairline bg-paper-50 p-8"
        >
          <span class="block font-mono text-[11px] text-sage-700 tabular-nums mb-4" aria-hidden="true">
            {{ String(i + 1).padStart(2, '0') }}
          </span>
          <h3 class="font-display text-2xl font-light leading-tight text-paper-900 mb-3">
            {{ step.title }}
          </h3>
          <p class="font-sans text-base text-paper-900/80 leading-relaxed">
            {{ step.text }}
          </p>
          <ul v-if="stepLinks[step.key].length > 0" class="mt-4 flex flex-wrap gap-x-5 gap-y-2">
            <li v-for="link in stepLinks[step.key]" :key="link.to">
              <NuxtLink
                :to="link.to"
                class="font-sans text-sm font-medium text-sage-700 underline underline-offset-4 hover:text-sage-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700"
              >
                {{ link.label }}
              </NuxtLink>
            </li>
          </ul>
        </li>
      </ol>
    </div>
  </section>
</template>
